import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdVpnLock } from "react-icons/md";
import { useProfileStore } from "../../store/useUserStore";
import { BiSolidCheckShield } from "react-icons/bi";
import axios from "axios";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email required"),
  otp: Yup.string().required("OTP required"),
  password: Yup.string()
    .min(6, "Password must be 6+ characters")
    .required("Password required"),
  terms: Yup.boolean()
    .oneOf([true], "You must accept the terms and conditions")
    .required("You must accept the terms and conditions"),
});

const RegisterParent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    loading,
    setLoading,
    parentActivateInfo,
    submitParentActivate,
    childVerify,
  } = useProfileStore();

  const handleRegister = async (values: any) => {
    const payload = {
      mode: "complete_activation",
      email: values.email,
      otp: values.otp,
      password: values.password,
    };
    try {
      setLoading(true);
      const result = await submitParentActivate(payload, "complete");
      if (result.success) {
        navigate("/login");
      }
    } catch (error) {
      console.error("Activation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateOTP = async (email) => {
    try {
      const otpResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/generate-otp`,
        {
          email: email,
        },
      );

      console.log("OTP response:", otpResponse);
      toast.success(t("register.otp_sent"));
    } catch (otpError: any) {
      console.log("OTP error:", otpError);
      if (otpError.response?.data?.message) {
        toast.error(otpError.response.data.message);
      } else {
        toast.error(t("server_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!childVerify) {
      navigate("/login#activate");
    }
  }, []);

  console.log("childVerify", childVerify);

  return (
    <div className="pt-4 px-4 h-screen flex flex-col">
      <header className="pt-4 px-0 flex flex-col gap-3 items-start">
        <Link to="/" className="inline-flex">
          <img src="/assets/img/koku_small.png" className="h-7" alt="logo" />
        </Link>

        <Link to="/login" className="inline-flex">
          <IoMdArrowRoundBack className="text-2xl" />
        </Link>
      </header>

      <div className="px-0 flex-grow flex flex-col">
        {parentActivateInfo && parentActivateInfo.verified_parent && (
          <>
            <h3 className="text-4xl font-bold text-black self-center mt-5 mb-8">
              {t("activate.confirmYourDetails")}
            </h3>

            <div className="mb-7 border rounded-xl p-4 bg-[#004aad] text-white flex flex-col items-center justify-center self-center w-full py-8">
              <MdVpnLock className="text-3xl my-3" />
              <p>
                {t("activate.parentName")}: {parentActivateInfo.parent_name}
              </p>
              <p>NRIC: {parentActivateInfo.parent_ic}</p>
            </div>
          </>
        )}
        <Formik
          initialValues={{ email: "", otp: "", password: "", terms: false }}
          validationSchema={validationSchema}
          onSubmit={handleRegister}
        >
          {({ isSubmitting, values }) => (
            <Form className="flex-grow flex flex-col">
              <div className="mb-7">
                <label className="text-sm font-medium text-black block mb-2">
                  {t("activate.enterEmail")}
                </label>
                <div className="relative">
                  <Field
                    type="email"
                    name="email"
                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2 "
                  />
                  <button
                    type="button"
                    disabled={values.email === ""}
                    onClick={() => generateOTP(values.email)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm text-theme font-medium hover:underline"
                  >
                    {t("activate.getOtp")}
                  </button>
                </div>

                <ErrorMessage
                  name="email"
                  component="div"
                  className="mt-1 text-red-500 text-sm"
                />
              </div>

              <div className="mb-7">
                <label className="text-sm font-medium text-black block mb-2">
                  {t("activate.checkYourEmailForOtp")}
                </label>
                <Field
                  type="text"
                  name="otp"
                  className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                />
                <ErrorMessage
                  name="otp"
                  component="div"
                  className="mt-1 text-red-500 text-sm"
                />
              </div>

              <div className="mb-7">
                <label className="text-sm font-medium text-black block mb-2">
                  {t("activate.setUpPassword")}
                </label>
                <Field
                  type="password"
                  name="password"
                  className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="mt-1 text-red-500 text-sm"
                />
              </div>

              <div className="mb-7">
                <label className="flex items-center space-x-2 text-sm">
                  <Field
                    type="checkbox"
                    name="terms"
                    className="w-4 h-4 text-theme focus:ring-theme rounded"
                  />
                  <span className="text-gray-700 ">
                    {t("register_new.agree_text")}{" "}
                    <a
                      href="https://mykokurikulum.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-theme underline hover:text-theme/80 transition"
                    >
                      {t("register_new.terms_and_conditions")}
                    </a>
                  </span>
                </label>
                <ErrorMessage
                  name="terms"
                  component="div"
                  className="text-red-500 text-xs"
                />
              </div>

              <div className="flex-grow"></div>

              <div className="mt-auto">
                <button
                  type="submit"
                  disabled={loading || isSubmitting}
                  className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center mb-4"
                >
                  {loading || isSubmitting ? (
                    "Loading..."
                  ) : (
                    <>
                      <BiSolidCheckShield className="mx-1 text-xl" />
                      {t("activate.activate_now")}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default RegisterParent;
